import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import {
  Code,
  Copy,
  Check,
  RefreshCw,
  Globe,
  Lock,
  Shield,
  Plus,
  X,
  Eye,
  Palette,
  ExternalLink,
  Loader2,
  Info,
} from 'lucide-react';
import PlanUpgradeOverlay from './plan/PlanUpgradeOverlay';
import { API_URL } from '../config';

export default function EmbedSettings({ organizationId, spaceId, spaceName }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [hideNavbarInEmbed, setHideNavbarInEmbed] = useState(false);

  // Custom branding state
  const [customBranding, setCustomBranding] = useState({
    logo_url: '',
    favicon_url: '',
    // Dark-mode variants: carried through to the embed surface, which falls back
    // to the light asset when these are empty.
    logo_dark_url: '',
    favicon_dark_url: '',
    primary_color: '#2563eb',
    secondary_color: '#64748b',
    custom_css: ''
  });

  useEffect(() => {
    fetchSettings();
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/embed-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
      if (response.data.custom_branding) {
        setCustomBranding(response.data.custom_branding);
      }
    } catch (error) {
      console.error('Failed to fetch embed settings:', error);
      toast.error('Failed to load embed settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}/subscription`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/embed-settings`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
      toast.success('Embed settings updated');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const regenerateApiKey = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/embed-settings/regenerate-key`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(prev => ({ ...prev, api_key: response.data.api_key }));
      toast.success('API key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate API key');
    }
  };

  const copyToClipboard = async (text, type) => {
    await navigator.clipboard.writeText(text);
    setCopiedCode(type);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const addDomain = () => {
    if (!newDomain.trim()) return;
    const domains = [...(settings.allowed_domains || []), newDomain.trim()];
    updateSettings({ allowed_domains: domains });
    setNewDomain('');
  };

  const removeDomain = (domain) => {
    const domains = settings.allowed_domains.filter(d => d !== domain);
    updateSettings({ allowed_domains: domains });
  };

  const hasBrandingAccess = subscription?.features?.custom_branding === true;
  /** Only show gate after we know plan (avoid flash while loading; hide if fetch failed). */
  const showEmbedBrandingGate =
    !loading && !!subscription && subscription?.features?.custom_branding === false;

  const withNavbarQuery = (url) => {
    if (!hideNavbarInEmbed || !url) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}navbar=0`;
  };

  const iframeCode = (() => {
    const raw = settings?.iframe_code || '';
    if (!hideNavbarInEmbed || !raw) return raw;
    return raw.replace(/src="([^"]+)"/i, (_, src) => `src="${withNavbarQuery(src)}"`);
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="embed-settings">
      {/* Enable/Disable Embed */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                Embed Documentation
              </CardTitle>
              <CardDescription className="mt-1">
                Allow embedding your documentation in external websites and apps
              </CardDescription>
            </div>
            <Switch
              checked={settings?.enabled || false}
              onCheckedChange={(checked) => updateSettings({ enabled: checked })}
              data-testid="embed-enabled-switch"
            />
          </div>
        </CardHeader>
      </Card>

      {settings?.enabled && (
        <>
          {/* Security Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Security Settings
              </CardTitle>
              <CardDescription>
                Control who can embed your documentation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Security Type</Label>
                <Select
                  value={settings?.security_type || 'public'}
                  onValueChange={(value) => updateSettings({ security_type: value })}
                >
                  <SelectTrigger data-testid="security-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-green-500" />
                        <div>
                          <div className="font-medium">Public</div>
                          <div className="text-xs text-slate-500">Anyone can embed</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="private">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-amber-500" />
                        <div>
                          <div className="font-medium">Private (API Key)</div>
                          <div className="text-xs text-slate-500">Requires API key</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="domain_restricted">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500" />
                        <div>
                          <div className="font-medium">Domain Restricted</div>
                          <div className="text-xs text-slate-500">Only allowed domains</div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* API Key Section (for private mode) */}
              {settings?.security_type === 'private' && (
                <div className="space-y-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <Label className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    API Key
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      value={settings?.api_key || ''}
                      readOnly
                      className="font-mono text-sm"
                      data-testid="embed-api-key"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(settings?.api_key, 'api_key')}
                    >
                      {copiedCode === 'api_key' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Regenerate API Key?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will invalidate the current API key. All existing embeds using the old key will stop working.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={regenerateApiKey}>Regenerate</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Include this key in your embed code to authenticate access
                  </p>
                </div>
              )}

              {/* Domain Whitelist (for domain restricted mode) */}
              {settings?.security_type === 'domain_restricted' && (
                <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Allowed Domains
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="example.com or *.example.com"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                      data-testid="domain-input"
                    />
                    <Button onClick={addDomain} data-testid="add-domain-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add
                    </Button>
                  </div>
                  {settings?.allowed_domains?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {settings.allowed_domains.map((domain) => (
                        <Badge
                          key={domain}
                          variant="secondary"
                          className="flex items-center gap-1 pr-1"
                        >
                          {domain}
                          <button
                            onClick={() => removeDomain(domain)}
                            className="ml-1 p-0.5 hover:bg-slate-300 dark:hover:bg-slate-600 rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Use *.example.com to allow all subdomains
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom Branding — Growth+ */}
          <div className="relative rounded-xl">
            <Card
              className={showEmbedBrandingGate ? 'pointer-events-none opacity-60' : ''}
              data-testid="embed-custom-branding-card"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Palette className="w-5 h-5" />
                      Custom Embed Branding
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Use different branding for embedded documentation
                    </CardDescription>
                  </div>
                  <Switch
                    checked={settings?.use_custom_branding || false}
                    onCheckedChange={(checked) => {
                      if (!hasBrandingAccess && checked) {
                        toast.error('Custom embed branding requires Growth or Corporate plan');
                        return;
                      }
                      updateSettings({ use_custom_branding: checked });
                    }}
                    disabled={!hasBrandingAccess}
                    data-testid="custom-branding-switch"
                  />
                </div>
              </CardHeader>
              {settings?.use_custom_branding && hasBrandingAccess && (
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={customBranding.primary_color}
                          onChange={(e) => setCustomBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={customBranding.primary_color}
                          onChange={(e) => setCustomBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={customBranding.secondary_color}
                          onChange={(e) => setCustomBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={customBranding.secondary_color}
                          onChange={(e) => setCustomBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Custom CSS</Label>
                    <Textarea
                      placeholder="/* Add custom styles for embedded docs */"
                      value={customBranding.custom_css || ''}
                      onChange={(e) => setCustomBranding(prev => ({ ...prev, custom_css: e.target.value }))}
                      className="font-mono text-sm h-24"
                    />
                  </div>
                  <Button
                    onClick={() => updateSettings({ custom_branding: customBranding })}
                    disabled={saving}
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Branding
                  </Button>
                </CardContent>
              )}
            </Card>
            {showEmbedBrandingGate && (
              <PlanUpgradeOverlay
                title="Custom embed branding"
                description="Colors, logo, and CSS for embedded docs are available on Growth and Corporate plans."
              />
            )}
          </div>

          {/* Embed Codes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                Embed Code
              </CardTitle>
              <CardDescription>
                Copy the code snippet to embed your documentation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="iframe" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="iframe">iframe Embed</TabsTrigger>
                  <TabsTrigger value="widget">JavaScript Widget</TabsTrigger>
                </TabsList>
                
                <TabsContent value="iframe" className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div>
                      <Label htmlFor="hide-navbar-embed" className="text-sm font-medium">
                        Hide navbar in embed
                      </Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Adds <code className="text-[11px]">?navbar=0</code> so the host site can use its own chrome.
                        The menu button stays available to open the sidebar.
                      </p>
                    </div>
                    <Switch
                      id="hide-navbar-embed"
                      checked={hideNavbarInEmbed}
                      onCheckedChange={setHideNavbarInEmbed}
                      data-testid="hide-navbar-embed-switch"
                    />
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">iframe Code</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(iframeCode, 'iframe')}
                      >
                        {copiedCode === 'iframe' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        Copy
                      </Button>
                    </div>
                    <ScrollArea className="h-24">
                      <pre className="text-xs font-mono text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                        {iframeCode}
                      </pre>
                    </ScrollArea>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>
                      Paste this code in your HTML where you want the documentation to appear.
                      Adjust the width and height attributes as needed. You can also add{' '}
                      <code className="text-xs">?navbar=0</code> or{' '}
                      <code className="text-xs">?hide_navbar=1</code> to the iframe URL manually.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="widget" className="space-y-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Widget Code</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(settings?.widget_code, 'widget')}
                      >
                        {copiedCode === 'widget' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        Copy
                      </Button>
                    </div>
                    <ScrollArea className="h-32">
                      <pre className="text-xs font-mono text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                        {settings?.widget_code}
                      </pre>
                    </ScrollArea>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>
                      Add this script to your page. It will create a floating help button
                      that opens your documentation in a sidebar.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              {settings?.security_type === 'private' && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    <span>
                      For private embeds, add <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">?api_key=YOUR_API_KEY</code> to the embed URL
                    </span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Preview
              </CardTitle>
              <CardDescription>
                See how your embedded documentation will look
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  const previewUrl = settings?.iframe_code?.match(/src="([^"]+)"/)?.[1];
                  if (previewUrl) {
                    window.open(previewUrl, '_blank');
                  }
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Embed Preview
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
